import { useState, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useDealerAuth } from '../context/DealerAuthContext';
import { useTheme } from '@/context/ThemeContext';
import DealerSidebar from './DealerSidebar';
import DealerTopbar from './DealerTopbar';
import PageLoader from '@/components/base/PageLoader';

interface Props {
  title: string;
  children: ReactNode;
}

export default function DealerLayout({ title, children }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const { isLoggedIn, loading, configError } = useDealerAuth();
  const { theme } = useTheme();
  const dark = theme === 'dark';

  if (loading) {
    return <PageLoader title="Checking dealer session" message="Loading your dealer portal." />;
  }

  if (configError) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-6 ${dark ? 'bg-[#111D2B]' : 'bg-gray-50'}`}>
        <div className={`${dark ? 'bg-[#1C2B3A] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900'} border rounded-2xl p-8 max-w-md text-center`}>
          <div
            className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-white mb-4"
            style={{ backgroundColor: 'var(--accent)' }}
          >
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <p className="text-xl font-black mb-2">Backend not configured</p>
          <p className={`${dark ? 'text-white/60' : 'text-gray-500'} text-sm`}>{configError}</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/dealer" replace />;
  }

  return (
    <div className={`flex h-screen overflow-hidden ${dark ? 'bg-[#111D2B]' : 'bg-gray-50'}`}>
      <DealerSidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <DealerTopbar title={title} />
        <main data-scroll-root className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
